import React from 'react';
import { IconDiamond, IconLocation } from '@noths/polaris-client-ribbons-design-system';
import { getContentstackImageURL } from '@noths/polaris-client-utils';

import { formatNumberOfOrders, MINIMUM_NUMBER_OF_ORDERS_TO_DISPLAY } from './formatNumberOfOrders';
import { formatRating } from './formatRating';
import { getPartnerDuration } from './getPartnerDuration';
import * as styles from './PartnerPageHeader.styles';

interface InfoItemProps {
  borderColor?: string;
  label: string;
  textColor?: string;
  value: React.ReactNode;
}

const InfoItem = ({ borderColor, label, textColor, value }: InfoItemProps) => {
  return (
    <>
      <span css={styles.infoItemValue({ borderColor, textColor })}>
        <b>{value}</b>
      </span>{' '}
      {label}
    </>
  );
};

export interface PartnerPageHeaderProps {
  coverImageSrc?: string;
  introText?: string;
  location?: string;
  logoImageSrc: string;
  name: string;
  numberOfOrders?: number;
  rating?: number;
  startDate?: string;
  topBrand?: boolean;
}

export const imageFallbackSrc =
  'https://images.contentstack.io/v3/assets/blt63f2f7afd19b09c2/blt9c18b5b63e1cc409/RibbonsPartnerPageHeaderFallback.png';

export const PartnerPageHeader = ({
  coverImageSrc,
  introText,
  location,
  logoImageSrc,
  name,
  numberOfOrders = 0,
  rating = -1,
  startDate,
  topBrand = false,
}: PartnerPageHeaderProps) => {
  const logoUrl = logoImageSrc ? getContentstackImageURL(logoImageSrc, 224) : imageFallbackSrc;
  const coverImageUrl = coverImageSrc
    ? getContentstackImageURL(coverImageSrc, 768, {
        aspectRatio: '16:9',
      })
    : imageFallbackSrc;
  const formattedNumberOfOrders =
    numberOfOrders >= MINIMUM_NUMBER_OF_ORDERS_TO_DISPLAY
      ? formatNumberOfOrders(numberOfOrders)
      : null;
  const duration = startDate ? getPartnerDuration(startDate) : null;
  const hasDuration = duration && duration.count > 0;
  const showInfoStrip = formattedNumberOfOrders || rating >= 0 || hasDuration;

  return (
    <header css={styles.root}>
      <div css={styles.coverImageBackground(coverImageUrl)}>
        <div css={[styles.logo, styles.logoOnCoverImage]}>
          <img alt={`${name} logo`} src={logoUrl} />
        </div>
      </div>
      {showInfoStrip && (
        <ul css={styles.infoStrip}>
          {formattedNumberOfOrders && (
            <li>
              <InfoItem label="ORDERS" value={formattedNumberOfOrders} />
            </li>
          )}
          {rating >= 0 && (
            <li>
              <InfoItem
                label="BRAND RATING"
                value={`${formatRating(rating)}/5`}
                {...styles.getRatingColors(rating)}
              />
            </li>
          )}
          {hasDuration && (
            <li>
              <InfoItem
                label="ON NOTHS"
                value={`${duration.count} ${duration.units === 'years' ? 'YEAR' : 'MONTH'}${
                  duration.count > 1 ? 'S' : ''
                }`}
              />
            </li>
          )}
        </ul>
      )}
      <div css={styles.columns}>
        <div css={styles.introTextColumn}>
          <div css={[styles.logo, styles.logoWithIntroText]}>
            <img alt={`${name} logo`} src={logoUrl} />
          </div>
          <h1 css={styles.heading}>{name}</h1>
          {location && (
            <p css={styles.location}>
              <IconLocation color="var(--brand-purple-base)" height={16} width={16} />
              {location}
            </p>
          )}
          {introText && <p css={styles.introText}>{introText}</p>}
          {topBrand && (
            <div css={styles.topBrand}>
              <strong>
                <IconDiamond color="var(--brand-purple-base)" height={24} width={24} />
                Top Brand
              </strong>
              <p>
                Awarded for consistently high-rated products, prompt replies, and products that
                arrive as promised.
              </p>
            </div>
          )}
        </div>
        {coverImageSrc && (
          <div css={styles.coverImage}>
            <img alt="" src={coverImageUrl} />
          </div>
        )}
      </div>
    </header>
  );
};
